// @ts-ignore
import uuid from 'uuid/v1';
import { JsonRpcId, JsonRpcMessage, LspCompletionItem, LspDiagnostic, LspHover, LspPosition } from './CSharpLspTypes';

type ActiveDocument = {
    opened: boolean;
    openedVersion: number;
    text: string;
    uri: string;
    version: number;
};

type PendingRequest = {
    reject: (reason?: unknown) => void;
    resolve: (value: unknown) => void;
};

type CSharpLspClientOptions = {
    createUrl: () => string;
    onDiagnostics: (uri: string, diagnostics: LspDiagnostic[]) => void;
    onDocumentWarm: (uri: string) => void;
    rootUri: string;
    workspaceName: string;
};

export class CSharpLspClient {
    private activeDocument?: ActiveDocument;

    private connectPromise?: Promise<void>;

    private disposed = false;

    private readonly pending = new Map<JsonRpcId, PendingRequest>();

    private reconnectDelayMs = 1000;

    private reconnectHandle?: number;

    private requestId = 0;

    private socket?: WebSocket;

    constructor(private readonly options: CSharpLspClientOptions) {}

    get activeUri(): string | undefined {
        return this.activeDocument ? this.activeDocument.uri : undefined;
    }

    dispose() {
        this.disposed = true;
        this.rejectPending(new Error('LSP client disposed'));

        if (this.reconnectHandle) {
            window.clearTimeout(this.reconnectHandle);
            this.reconnectHandle = undefined;
        }

        if (this.socket) {
            this.socket.close();
            this.socket = undefined;
        }
    }

    async openDocument(text: string): Promise<void> {
        const previousDocument = this.activeDocument;

        if (previousDocument?.opened) {
            this.notify('textDocument/didClose', {
                textDocument: {
                    uri: previousDocument.uri
                }
            });
            previousDocument.opened = false;
        }

        const nextDocument: ActiveDocument = {
            opened: false,
            openedVersion: 0,
            text,
            uri: this.createDocumentUri(),
            version: 1
        };

        this.activeDocument = nextDocument;
        this.options.onDiagnostics(nextDocument.uri, []);

        await this.ensureReady();

        if (this.activeDocument !== nextDocument) {
            return;
        }

        this.openDocumentIfNeeded(nextDocument);
    }

    async requestCompletion(position: LspPosition): Promise<LspCompletionItem[]> {
        if (!this.activeDocument) {
            return [];
        }

        const response = await this.request('textDocument/completion', {
            context: {
                triggerKind: 1
            },
            position,
            textDocument: {
                uri: this.activeDocument.uri
            }
        });

        const result = (response as { result?: unknown }).result;
        if (Array.isArray(result)) {
            return result as LspCompletionItem[];
        }

        if (result && typeof result === 'object' && Array.isArray((result as { items?: unknown[] }).items)) {
            return (result as { items: LspCompletionItem[] }).items;
        }

        return [];
    }

    async requestHover(position: LspPosition): Promise<LspHover | null> {
        if (!this.activeDocument) {
            return null;
        }

        const response = await this.request('textDocument/hover', {
            position,
            textDocument: {
                uri: this.activeDocument.uri
            }
        });

        return ((response as { result?: LspHover | null }).result || null) as LspHover | null;
    }

    async syncDocument(text: string): Promise<void> {
        const document = this.activeDocument;

        if (!document) {
            return;
        }

        document.text = text;

        if (!document.opened) {
            return;
        }

        const requestedVersion = document.version + 1;
        document.version = requestedVersion;
        await this.ensureReady();

        if (
            this.activeDocument !== document ||
            requestedVersion !== document.version ||
            document.openedVersion === document.version
        ) {
            return;
        }

        this.notify('textDocument/didChange', {
            contentChanges: [
                {
                    text
                }
            ],
            textDocument: {
                uri: document.uri,
                version: requestedVersion
            }
        });
    }

    private ensureReady(): Promise<void> {
        if (this.disposed) {
            throw new Error('LSP client disposed');
        }

        if (this.connectPromise) {
            return this.connectPromise;
        }

        this.connectPromise = new Promise<void>((resolve, reject) => {
            const socket = new WebSocket(this.options.createUrl());
            this.socket = socket;

            socket.addEventListener('open', async () => {
                try {
                    await this.request(
                        'initialize',
                        {
                            capabilities: {
                                textDocument: {
                                    completion: {
                                        completionItem: {
                                            snippetSupport: false
                                        }
                                    }
                                },
                                workspace: {
                                    workspaceFolders: true
                                }
                            },
                            processId: null,
                            rootUri: this.options.rootUri,
                            workspaceFolders: [
                                {
                                    name: this.options.workspaceName,
                                    uri: this.options.rootUri
                                }
                            ]
                        },
                        true
                    );

                    this.notify('initialized', {});
                    this.reconnectDelayMs = 1000;
                    this.handleConnectionReady();
                    resolve();
                } catch (error) {
                    socket.close();
                    reject(error);
                }
            });

            socket.addEventListener('close', () => {
                this.socket = undefined;
                this.connectPromise = undefined;
                this.rejectPending(new Error('LSP socket closed'));

                if (!this.disposed) {
                    this.scheduleReconnect();
                }
            });

            socket.addEventListener('error', () => {
                if (!this.connectPromise) {
                    return;
                }

                reject(new Error('Unable to connect to LSP service'));
            });

            socket.addEventListener('message', event => {
                this.handleMessage(event.data);
            });
        });

        return this.connectPromise;
    }

    private notify(method: string, params: unknown) {
        if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
            return;
        }

        this.socket.send(
            JSON.stringify({
                jsonrpc: '2.0',
                method,
                params
            })
        );
    }

    private request(method: string, params: unknown, bypassReadyCheck?: boolean): Promise<unknown> {
        const execute = () =>
            new Promise<unknown>((resolve, reject) => {
                if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
                    reject(new Error('LSP socket is not open'));
                    return;
                }

                const id = ++this.requestId;
                this.pending.set(id, {
                    reject,
                    resolve
                });

                this.socket.send(
                    JSON.stringify({
                        id,
                        jsonrpc: '2.0',
                        method,
                        params
                    })
                );
            });

        return bypassReadyCheck ? execute() : this.ensureReady().then(() => execute());
    }

    private handleConnectionReady() {
        if (!this.activeDocument) {
            return;
        }

        this.activeDocument.opened = false;
        this.openDocumentIfNeeded(this.activeDocument);
    }

    private handleMessage(rawMessage: MessageEvent['data']) {
        if (typeof rawMessage !== 'string') {
            return;
        }

        let message: JsonRpcMessage;

        try {
            message = JSON.parse(rawMessage) as JsonRpcMessage;
        } catch (error) {
            console.error('Failed to parse LSP message', error);
            return;
        }

        if (message.method && isJsonRpcId(message.id)) {
            this.handleServerRequest(message);
            return;
        }

        if (isJsonRpcId(message.id) && this.pending.has(message.id)) {
            const pending = this.pending.get(message.id)!;
            this.pending.delete(message.id);

            if (message.error) {
                pending.reject(new Error(message.error.message || 'LSP request failed'));
            } else {
                pending.resolve(message);
            }

            return;
        }

        if (message.method) {
            this.handleNotification(message);
        }
    }

    private handleNotification(message: JsonRpcMessage) {
        if (message.method !== 'textDocument/publishDiagnostics') {
            return;
        }

        const params = (message.params || {}) as { diagnostics?: LspDiagnostic[]; uri?: string };
        if (!params.uri || params.uri !== this.activeUri) {
            return;
        }

        this.options.onDocumentWarm(params.uri);
        this.options.onDiagnostics(params.uri, params.diagnostics || []);
    }

    private handleServerRequest(message: JsonRpcMessage) {
        const id = message.id;

        if (!isJsonRpcId(id)) {
            return;
        }

        if (message.method === 'workspace/configuration') {
            this.respond(id, []);
            return;
        }

        if (message.method === 'client/registerCapability' || message.method === 'client/unregisterCapability') {
            this.respond(id, null);
            return;
        }

        this.respondError(id, -32601, `Unsupported method: ${message.method}`);
    }

    private openDocumentIfNeeded(document: ActiveDocument) {
        if (document.opened) {
            return;
        }

        this.notify('textDocument/didOpen', {
            textDocument: {
                languageId: 'csharp',
                text: document.text,
                uri: document.uri,
                version: document.version
            }
        });

        document.opened = true;
        document.openedVersion = document.version;
    }

    private createDocumentUri(): string {
        return `${this.options.rootUri}/src/JE.Alpha.DotNet.Shared.EntryPoint/${uuid()}.cs`;
    }

    private rejectPending(error: Error) {
        for (const pending of this.pending.values()) {
            pending.reject(error);
        }

        this.pending.clear();
    }

    private respond(id: JsonRpcId, result: unknown) {
        if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
            return;
        }

        this.socket.send(
            JSON.stringify({
                id,
                jsonrpc: '2.0',
                result
            })
        );
    }

    private respondError(id: JsonRpcId, code: number, message: string) {
        if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
            return;
        }

        this.socket.send(
            JSON.stringify({
                error: {
                    code,
                    message
                },
                id,
                jsonrpc: '2.0'
            })
        );
    }

    private scheduleReconnect() {
        if (this.reconnectHandle) {
            return;
        }

        this.reconnectHandle = window.setTimeout(() => {
            this.reconnectHandle = undefined;

            if (!this.disposed) {
                void this.ensureReady().catch(() => {
                    this.reconnectDelayMs = Math.min(this.reconnectDelayMs * 1.5, 10000);
                    this.scheduleReconnect();
                });
            }
        }, this.reconnectDelayMs);
    }
}

function isJsonRpcId(value: JsonRpcMessage['id']): value is JsonRpcId {
    return typeof value === 'number' || typeof value === 'string';
}
